/**
 * AdminSubscriptionsTab Component
 * Full subscription management interface with filtering, approval, revoke, and modify capabilities
 * 
 * Features:
 * - View all subscriptions with filters (status, approval_status, tier, search)
 * - Quick filter tabs: All, Pending, Active, Cancelled
 * - Approve/Decline pending requests
 * - Revoke active subscriptions with refund options
 * - Modify subscription details (tier, status, dates)
 * 
 * Props:
 * - selectedId: Optional ID to highlight a specific subscription (from URL /admin/subscriptions/:id)
 */
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { formatDateShort, formatDate, formatDateTime, formatTimeAgo } from '../../utils/dateUtils.js';
import {
  getSubscriptions,
  approveSubscription,
  declineSubscription,
  revokeSubscription,
  adminReactivateSubscription,
  modifySubscription,
  grantSubscription,
  getAllUsers,
  reviewQuote,
  confirmPayment,
  sendPaymentReminder,
  getDemoConfig
} from '../../services/adminService.js';
import { getAdminRole } from '../../state/userState.js';
import { getPricing } from '../../services/subscriptionService.js';

// Fallback demo length, used ONLY if GET /admin/demo-config is unreachable.
// The real value is `demo_duration_days` from that endpoint — it is tunable
// from the Demo tab at runtime (DB-backed, no deploy), so this must never be
// treated as the source of truth. Kept in sync with settings.demo_duration_days
// so an offline default still matches "our" demo length rather than inventing
// a shorter one (this modal used to default DEMO grants to 7 days).
const DEMO_DURATION_DAYS_FALLBACK = 21;

// Status and tier configurations
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'TRIALING', label: 'Trialing' }
];

const APPROVAL_OPTIONS = [
  { value: '', label: 'All Approval' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUOTE_SENT', label: 'Quote Sent' },
  { value: 'QUOTE_ACCEPTED', label: 'Quote Accepted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'NONE', label: 'None' }
];

// Human-readable approval status labels and badge classes
const APPROVAL_BADGE_CONFIG = {
  PENDING: { label: 'Pending Review', cssClass: 'admin-badge--warning' },
  QUOTE_SENT: { label: 'Quote Sent', cssClass: 'admin-badge--info' },
  QUOTE_ACCEPTED: { label: 'Awaiting Payment', cssClass: 'admin-badge--success' },
  APPROVED: { label: 'Active', cssClass: 'admin-badge--success' },
  DECLINED: { label: 'Declined', cssClass: 'admin-badge--error' },
};

const TIER_OPTIONS = [
  { value: '', label: 'All Tiers' },
  { value: 'FREE', label: 'Free' },
  { value: 'PRO', label: 'Pro' },
  { value: 'DEMO', label: 'Demo' }
];

const REFUND_OPTIONS = [
  { value: 'full', label: 'Full Refund', description: 'Refund the entire payment amount' },
  { value: 'prorated', label: 'Prorated Refund', description: 'Refund based on remaining subscription time' },
  { value: 'none', label: 'No Refund', description: 'Cancel without issuing a refund' }
];

export function AdminSubscriptionsTab({ selectedId, initialFilters = {} }) {
  const adminRole = getAdminRole();
  const canApproveDecline = ['admin', 'customer_success', 'finance', 'application_specialist'].includes(adminRole);
  const canManageFinancial = ['admin', 'finance'].includes(adminRole);

  // Data state
  const [subscriptions, setSubscriptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingId, setProcessingId] = useState(null);
  
  // Filter state - initialize from URL params
  // Two-state search: searchInput is the immediate, controlled input value
  // (rendered every keystroke), filters.search is the debounced query that
  // actually triggers the API call. Keeps the input snappy without pounding
  // the backend on every keypress.
  const [searchInput, setSearchInput] = useState(initialFilters.search || '');
  const [filters, setFilters] = useState({
    status: initialFilters.status || '',
    approval_status: initialFilters.approval_status || '',
    tier: initialFilters.tier || '',
    search: initialFilters.search || ''
  });
  
  // Determine initial quick filter based on URL params
  const getInitialQuickFilter = () => {
    if (initialFilters.approval_status === 'PENDING') return 'pending';
    if (initialFilters.status === 'ACTIVE') return 'active';
    if (initialFilters.status === 'CANCELLED') return 'cancelled';
    return 'all';
  };
  const [activeQuickFilter, setActiveQuickFilter] = useState(getInitialQuickFilter());
  const [offset, setOffset] = useState(0);
  const limit = 50;
  
  // Modal states
  const [declineModal, setDeclineModal] = useState({ open: false, userId: null, email: '' });
  const [declineReason, setDeclineReason] = useState('');
  
  const [revokeModal, setRevokeModal] = useState({ open: false, userId: null, email: '', tier: '' });
  const [revokeForm, setRevokeForm] = useState({ reason: '', refund_type: 'full', notify_user: true });
  
  const [modifyModal, setModifyModal] = useState({ open: false, subscription: null });
  const [modifyForm, setModifyForm] = useState({});
  
  // Quick Actions modal state
  const [quickActionsModal, setQuickActionsModal] = useState({ open: false, subscription: null });
  const [giveAccessModal, setGiveAccessModal] = useState({ open: false, subscription: null, tier: 'PRO' });
  const [giveAccessForm, setGiveAccessForm] = useState({ until: '', notify_user: true });

  // Ad-hoc "Grant Access to User" lookup modal — reaches users who don't yet
  // have a subscription row (and therefore never appear in the list above,
  // since it's built from an inner join on Subscription).
  const [userLookupModal, setUserLookupModal] = useState({ open: false });
  const [userLookupQuery, setUserLookupQuery] = useState('');
  const [userLookupResults, setUserLookupResults] = useState([]);
  const [userLookupLoading, setUserLookupLoading] = useState(false);
  const [userLookupError, setUserLookupError] = useState(null);

  // Quote review modal state (B2B workflow)
  const [quoteReviewModal, setQuoteReviewModal] = useState({ open: false, subscription: null });
  const [quoteReviewForm, setQuoteReviewForm] = useState({
    action: 'approve',
    discount_percent: 0,
    custom_amount: '',
    decline_reason: '',
    admin_notes: '',
    notify_user: true
  });
  
  // Dynamic pricing
  const [proBasePrice, setProBasePrice] = useState(4900);
  useEffect(() => {
    getPricing().then(p => setProBasePrice(p.yearly_price)).catch(() => {});
  }, []);

  // Demo length — read from the live demo config so "Give Demo" pre-fills the
  // SAME duration the Demo tab is configured with (and that a self-service
  // demo grant uses). Admins tune this at runtime, so a hardcoded default here
  // would silently drift from the configured value.
  const [demoDurationDays, setDemoDurationDays] = useState(DEMO_DURATION_DAYS_FALLBACK);
  useEffect(() => {
    getDemoConfig()
      .then((c) => {
        if (Number.isFinite(c?.demo_duration_days) && c.demo_duration_days > 0) {
          setDemoDurationDays(c.demo_duration_days);
        }
      })
      .catch(() => {});
  }, []);

  // Refs
  const selectedRef = useRef(null);
  const searchTimeout = useRef(null);
  const userLookupTimeout = useRef(null);

  // Load subscriptions
  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const filterParams = { ...filters, limit, offset };
      // Remove empty filters
      Object.keys(filterParams).forEach(key => {
        if (!filterParams[key]) delete filterParams[key];
      });
      
      const data = await getSubscriptions(filterParams);
      console.log('[AdminSubscriptions] API response:', data);
      
      // Handle both { items, total } and flat array responses
      const items = data.items || data;
      const total = data.total || items.length;
      
      setSubscriptions(Array.isArray(items) ? items : []);
      setTotalCount(total);
    } catch (err) {
      console.error('[AdminSubscriptions] Load error:', err);
      setError(err?.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  // Scroll to selected subscription
  useEffect(() => {
    if (selectedId && selectedRef.current && !loading) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedId, loading, subscriptions]);

  // Debounced search — 500ms is the empirical sweet-spot for B2B dashboards
  // (long enough to not fire on every keystroke, short enough to feel snappy).
  // Enter forces immediate submit, Esc clears.
  const commitSearch = (value) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setFilters(prev => ({ ...prev, search: value }));
    setOffset(0);
  };

  const handleSearchInput = (value) => {
    setSearchInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => commitSearch(value), 500);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitSearch(searchInput);
    } else if (e.key === 'Escape') {
      setSearchInput('');
      commitSearch('');
    }
  };

  // Quick filters
  const quickFilters = [
    { id: 'all', label: 'All', filters: { status: '', approval_status: '' } },
    { id: 'pending', label: 'Pending', filters: { approval_status: 'PENDING' } },
    { id: 'quote_sent', label: 'Quote Sent', filters: { approval_status: 'QUOTE_SENT' } },
    { id: 'awaiting_payment', label: 'Awaiting Payment', filters: { approval_status: 'QUOTE_ACCEPTED' } },
    { id: 'active', label: 'Active', filters: { status: 'ACTIVE' } },
    { id: 'cancelled', label: 'Cancelled', filters: { status: 'CANCELLED' } }
  ];

  const handleQuickFilter = (filterId) => {
    const qf = quickFilters.find(f => f.id === filterId);
    if (qf) {
      setActiveQuickFilter(filterId);
      setFilters(prev => ({ ...prev, status: '', approval_status: '', ...qf.filters }));
      setOffset(0);
    }
  };

  // Approve handler
  const handleApprove = async (userId, userEmail) => {
    if (!confirm(`Approve subscription request for ${userEmail}?`)) return;
    
    setProcessingId(userId);
    setError('');
    setSuccess('');
    
    try {
      await approveSubscription(userId);
      setSuccess(`Subscription approved for ${userEmail}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Approve error:', err);
      setError(err?.message || 'Failed to approve subscription');
    } finally {
      setProcessingId(null);
    }
  };

  // Decline modal handlers
  const openDeclineModal = (userId, email) => {
    setDeclineModal({ open: true, userId, email });
    setDeclineReason('');
  };

  const closeDeclineModal = () => {
    setDeclineModal({ open: false, userId: null, email: '' });
    setDeclineReason('');
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }
    
    const { userId, email } = declineModal;
    setProcessingId(userId);
    setError('');
    setSuccess('');
    closeDeclineModal();
    
    try {
      await declineSubscription(userId, declineReason);
      setSuccess(`Subscription declined for ${email}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Decline error:', err);
      setError(err?.message || 'Failed to decline subscription');
    } finally {
      setProcessingId(null);
    }
  };

  // Revoke modal handlers
  const openRevokeModal = (subscription) => {
    setRevokeModal({ 
      open: true, 
      userId: subscription.user_id, 
      email: subscription.user_email,
      tier: subscription.tier,
      amount: subscription.amount
    });
    setRevokeForm({ reason: '', refund_type: 'none', notify_user: true });
  };

  const closeRevokeModal = () => {
    setRevokeModal({ open: false, userId: null, email: '', tier: '', amount: 0 });
    setRevokeForm({ reason: '', refund_type: 'none', notify_user: true });
  };

  const handleRevoke = async () => {
    const { userId, email } = revokeModal;
    setProcessingId(userId);
    setError('');
    setSuccess('');
    closeRevokeModal();
    
    try {
      const result = await revokeSubscription(userId, revokeForm);
      const refundMsg = result.refund_amount ? ` (Refund: €${result.refund_amount.toFixed(2)})` : '';
      setSuccess(`Subscription revoked for ${email}${refundMsg}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Revoke error:', err);
      setError(err?.message || 'Failed to revoke subscription');
    } finally {
      setProcessingId(null);
    }
  };

  // Modify modal handlers
  const openModifyModal = (subscription) => {
    setModifyModal({ open: true, subscription });
    setModifyForm({
      tier: subscription.tier,
      status: subscription.status,
      approval_status: subscription.approval_status || 'NONE',
      current_period_end: subscription.current_period_end?.split('T')[0] || '',
      notify_user: false
    });
  };

  const closeModifyModal = () => {
    setModifyModal({ open: false, subscription: null });
    setModifyForm({});
  };

  const handleModify = async () => {
    const { subscription } = modifyModal;
    if (!subscription) return;
    
    // Build changes object with only modified fields
    const changes = {};
    if (modifyForm.tier !== subscription.tier) changes.tier = modifyForm.tier;
    if (modifyForm.status !== subscription.status) changes.status = modifyForm.status;
    if (modifyForm.approval_status !== (subscription.approval_status || 'NONE')) changes.approval_status = modifyForm.approval_status;
    if (modifyForm.current_period_end && modifyForm.current_period_end !== subscription.current_period_end?.split('T')[0]) {
      changes.current_period_end = modifyForm.current_period_end + 'T00:00:00Z';
    }
    if (modifyForm.notify_user) changes.notify_user = true;
    
    if (Object.keys(changes).filter(k => k !== 'notify_user').length === 0) {
      setError('No changes to save');
      return;
    }
    
    setProcessingId(subscription.user_id);
    setError('');
    setSuccess('');
    closeModifyModal();
    
    try {
      await modifySubscription(subscription.user_id, changes);
      setSuccess(`Subscription updated for ${subscription.user_email}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Modify error:', err);
      setError(err?.message || 'Failed to modify subscription');
    } finally {
      setProcessingId(null);
    }
  };

  // Quote review modal handlers (B2B workflow)
  const openQuoteReviewModal = (subscription) => {
    setQuoteReviewModal({ open: true, subscription });
    setQuoteReviewForm({
      action: 'approve',
      discount_percent: 0,
      custom_amount: '',
      decline_reason: '',
      admin_notes: '',
      notify_user: true
    });
  };

  const closeQuoteReviewModal = () => {
    setQuoteReviewModal({ open: false, subscription: null });
    setQuoteReviewForm({
      action: 'approve',
      discount_percent: 0,
      custom_amount: '',
      decline_reason: '',
      admin_notes: '',
      notify_user: true
    });
  };

  const handleQuoteReview = async () => {
    const { subscription } = quoteReviewModal;
    if (!subscription) return;
    
    // Validation
    if (quoteReviewForm.action === 'decline' && !quoteReviewForm.decline_reason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }
    
    setProcessingId(subscription.user_id);
    setError('');
    setSuccess('');
    closeQuoteReviewModal();
    
    try {
      const reviewData = {
        action: quoteReviewForm.action,
        notify_user: quoteReviewForm.notify_user,
        admin_notes: quoteReviewForm.admin_notes || null
      };
      
      if (quoteReviewForm.action === 'approve') {
        reviewData.discount_percent = quoteReviewForm.discount_percent || null;
        // Convert custom_amount to cents if provided
        if (quoteReviewForm.custom_amount) {
          reviewData.custom_amount_cents = Math.round(parseFloat(quoteReviewForm.custom_amount) * 100);
        }
      } else {
        reviewData.decline_reason = quoteReviewForm.decline_reason;
      }
      
      const result = await reviewQuote(subscription.user_id, reviewData);
      
      const actionLabel = quoteReviewForm.action === 'approve' ? 'approved' : 'declined';
      setSuccess(`Quote ${actionLabel} for ${subscription.user_email}${result.quote_url ? ' - Quote sent!' : ''}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Quote review error:', err);
      setError(err?.message || 'Failed to process quote review');
    } finally {
      setProcessingId(null);
    }
  };

  // Confirm bank transfer payment handler (B2B workflow)
  const handleConfirmPayment = async (userId, userEmail) => {
    if (!confirm(`Confirm payment received for ${userEmail}?\n\nThis will mark the invoice as paid and notify the customer.`)) return;
    
    setProcessingId(userId);
    setError('');
    setSuccess('');
    
    try {
      const result = await confirmPayment(userId);
      setSuccess(`Payment confirmed for ${userEmail} - Invoice ${result.invoice_id || ''} marked as paid`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Confirm payment error:', err);
      setError(err?.message || 'Failed to confirm payment');
    } finally {
      setProcessingId(null);
    }
  };

  // Send payment reminder handler (B2B workflow)
  const handleSendReminder = async (userId, userEmail, companyName) => {
    const customMessage = prompt(
      `Send payment reminder to ${userEmail} (${companyName || 'N/A'})?\n\nOptionally enter a custom message to include:`,
      ''
    );
    
    // User cancelled
    if (customMessage === null) return;
    
    setProcessingId(userId);
    setError('');
    setSuccess('');
    
    try {
      const result = await sendPaymentReminder(userId, customMessage || null);
      setSuccess(`Payment reminder #${result.reminder_count} sent to ${userEmail}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      console.error('[AdminSubscriptions] Send reminder error:', err);
      setError(err?.message || 'Failed to send payment reminder');
    } finally {
      setProcessingId(null);
    }
  };

  // Quick Actions modal handlers
  const openQuickActionsModal = (sub) => setQuickActionsModal({ open: true, subscription: sub });
  const closeQuickActionsModal = () => setQuickActionsModal({ open: false, subscription: null });

  const openGiveAccessModal = (sub, tier) => {
    const now = new Date();
    let defaultDate;
    if (tier === 'PRO') {
      defaultDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    } else {
      // DEMO: the configured demo length (21 days by default), NOT a shorter
      // ad-hoc window — an admin-granted demo should match the demo every
      // other path hands out.
      defaultDate = new Date(now.getTime() + demoDurationDays * 24 * 60 * 60 * 1000);
    }
    setGiveAccessModal({ open: true, subscription: sub, tier });
    setGiveAccessForm({ until: defaultDate.toISOString().split('T')[0], notify_user: true });
    closeQuickActionsModal();
  };
  const closeGiveAccessModal = () => setGiveAccessModal({ open: false, subscription: null, tier: 'PRO' });

  const handleGiveAccess = async () => {
    const { subscription, tier } = giveAccessModal;
    if (!subscription) return;
    setProcessingId(subscription.user_id);
    setError('');
    setSuccess('');
    closeGiveAccessModal();
    try {
      if (subscription.subscription_id == null) {
        // Ad-hoc grant: no subscription row exists yet (e.g. a seeded
        // knowledge-expert account, or any account created outside the
        // signup/create-demo flow) — /grant creates the FREE base row on
        // the fly, mirroring what the removed Users-page control did.
        const untilDate = new Date(giveAccessForm.until + 'T23:59:59Z');
        const days = Math.max(1, Math.ceil((untilDate - new Date()) / 86400000));
        await grantSubscription(subscription.user_id, tier, days);
      } else {
        await modifySubscription(subscription.user_id, {
          tier: tier,
          status: 'ACTIVE',
          approval_status: 'APPROVED',
          current_period_end: giveAccessForm.until + 'T23:59:59Z',
          notify_user: giveAccessForm.notify_user
        });
      }
      setSuccess(`${tier} access granted to ${subscription.user_email} until ${giveAccessForm.until}`);
      setTimeout(() => setSuccess(''), 5000);
      await loadSubscriptions();
    } catch (err) {
      setError(err?.message || `Failed to grant ${tier} access`);
    } finally {
      setProcessingId(null);
    }
  };

  // Ad-hoc "Grant Access to User" lookup modal handlers
  const openUserLookupModal = () => {
    setUserLookupQuery('');
    setUserLookupResults([]);
    setUserLookupLoading(false);
    setUserLookupError(null);
    setUserLookupModal({ open: true });
  };

  const closeUserLookupModal = () => {
    if (userLookupTimeout.current) clearTimeout(userLookupTimeout.current);
    setUserLookupModal({ open: false });
    setUserLookupQuery('');
    setUserLookupResults([]);
    setUserLookupError(null);
  };

  const handleUserLookupInput = (value) => {
    setUserLookupQuery(value);
    if (userLookupTimeout.current) clearTimeout(userLookupTimeout.current);
    if (!value.trim()) {
      setUserLookupResults([]);
      setUserLookupLoading(false);
      setUserLookupError(null);
      return;
    }
    userLookupTimeout.current = setTimeout(async () => {
      setUserLookupLoading(true);
      setUserLookupError(null);
      try {
        const results = await getAllUsers({ search: value, limit: 10 });
        setUserLookupResults(Array.isArray(results) ? results : []);
      } catch (err) {
        console.error('[AdminSubscriptions] User lookup error:', err);
        setUserLookupResults([]);
        // Surface a permission denial distinctly from a genuine empty
        // result — otherwise a 403 reads as "no users found".
        setUserLookupError(
          err.status === 403
            ? "You don't have permission to look up users."
            : 'User lookup failed. Please try again.'
        );
      } finally {
        setUserLookupLoading(false);
      }
    }, 500);
  };

  const handleUserLookupKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeUserLookupModal();
    }
  };

  // Opens the same Give Access modal used for existing subscriptions, but
  // with a synthetic subscription-like object that has no `subscription_id`
  // — that absence is the signal `handleGiveAccess` uses to call /grant
  // instead of /modify.
  const handlePickUserForGrant = (user, tier) => {
    closeUserLookupModal();
    openGiveAccessModal({ user_id: user.id, user_email: user.email }, tier);
  };

  // Calculate final price for quote review preview
  const getQuoteFinalPrice = () => {
    if (quoteReviewForm.custom_amount) {
      return parseFloat(quoteReviewForm.custom_amount);
    }
    if (quoteReviewForm.discount_percent) {
      return proBasePrice * (1 - quoteReviewForm.discount_percent / 100);
    }
    return proBasePrice;
  };

  // Utility functions
  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'admin-badge--success';
      case 'CANCELLED': return 'admin-badge--error';
      case 'PAST_DUE': return 'admin-badge--warning';
      case 'TRIALING': return 'admin-badge--info';
      case 'INACTIVE': return 'admin-badge--basic';
      default: return 'admin-badge--basic';
    }
  };

  const getApprovalBadgeClass = (approvalStatus) => {
    const config = APPROVAL_BADGE_CONFIG[approvalStatus?.toUpperCase()];
    return config ? config.cssClass : 'admin-badge--basic';
  };

  const getApprovalBadgeLabel = (approvalStatus) => {
    const config = APPROVAL_BADGE_CONFIG[approvalStatus?.toUpperCase()];
    return config ? config.label : (approvalStatus || 'NONE');
  };

  const getTierBadgeClass = (tier) => {
    const tierLower = String(tier || '').toLowerCase();
    if (tierLower === 'pro' || tierLower === 'premium') return 'admin-badge--premium';
    if (tierLower === 'demo') return 'admin-badge--info';
    return 'admin-badge--basic';
  };

  // Date formatting uses centralized utilities from dateUtils.js
  // formatDateShort, formatDateTime, formatTimeAgo are imported at top
  const formatDate = formatDateShort;
  const getTimeSince = formatTimeAgo;

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `€${Number(amount).toFixed(2)}`;
  };

  if (loading && subscriptions.length === 0) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading subscriptions...</p>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      {/* Header with stats */}
      <div className="admin-section-header">
        <div className="admin-section-title">
          <h2>Subscription Management</h2>
          <span className="admin-section-count">{totalCount} total</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canManageFinancial && (
            <button className="admin-btn admin-btn--primary" onClick={openUserLookupModal}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/>
                <line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
              Grant Access to User
            </button>
          )}
          <button className="admin-btn admin-btn--secondary" onClick={loadSubscriptions} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Quick filter tabs */}
      <div className="admin-quick-filters">
        {quickFilters.map(qf => (
          <button
            key={qf.id}
            className={`admin-quick-filter ${activeQuickFilter === qf.id ? 'admin-quick-filter--active' : ''}`}
            onClick={() => handleQuickFilter(qf.id)}
          >
            {qf.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="admin-filter-bar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search by email or name…  (Enter = search now · Esc = clear)"
            value={searchInput}
            onInput={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
        
        <select 
          className="admin-filter-select"
          value={filters.status}
          onChange={(e) => {
            setFilters(prev => ({ ...prev, status: e.target.value }));
            setActiveQuickFilter('');
            setOffset(0);
          }}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        
        <select 
          className="admin-filter-select"
          value={filters.approval_status}
          onChange={(e) => {
            setFilters(prev => ({ ...prev, approval_status: e.target.value }));
            setActiveQuickFilter('');
            setOffset(0);
          }}
        >
          {APPROVAL_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        
        <select 
          className="admin-filter-select"
          value={filters.tier}
          onChange={(e) => {
            setFilters(prev => ({ ...prev, tier: e.target.value }));
            setOffset(0);
          }}
        >
          {TIER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Alerts */}
      {success && (
        <div className="admin-alert admin-alert--success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <path d="M22 4L12 14.01l-3-3"/>
          </svg>
          {success}
        </div>
      )}

      {error && (
        <div className="admin-alert admin-alert--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
          {error}
          <button className="admin-alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Empty state */}
      {subscriptions.length === 0 && !loading ? (
        <div className="admin-empty-state admin-empty-state--glass">
          <div className="admin-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <h3>No subscriptions found</h3>
          <p>Try adjusting your filters or search query</p>
        </div>
      ) : (
        <>
          {/* Subscriptions table */}
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Approval</th>
                  <th>Amount</th>
                  <th>Period End</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => {
                  const isSelected = selectedId && (
                    String(sub.user_id) === String(selectedId) || 
                    String(sub.subscription_id) === String(selectedId)
                  );
                  const isPending = sub.approval_status === 'PENDING';
                  const isQuoteSent = sub.approval_status === 'QUOTE_SENT';
                  const isQuoteAccepted = sub.approval_status === 'QUOTE_ACCEPTED';
                  const isDeclined = sub.approval_status === 'DECLINED';
                  const isActive = sub.status === 'ACTIVE';
                  
                  return (
                    <tr 
                      key={sub.user_id || sub.subscription_id}
                      ref={isSelected ? selectedRef : null}
                      className={isSelected ? 'admin-table-row--selected' : ''}
                    >
                      <td>
                        <div className="admin-table-user">
                          <div className="user-avatar-small">
                            {(() => {
                              const titles = ['mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'dipl.-ing.', 'dipl.', 'ing.'];
                              const parts = String(sub.user_name || '').split(' ').filter(p => p && !titles.includes(p.toLowerCase()));
                              const f = (parts[0] || '').charAt(0);
                              const l = (parts[parts.length - 1] || '').charAt(0);
                              if (f && l && parts.length > 1) return (f + l).toUpperCase();
                              return (f || String(sub.user_email || '').charAt(0) || 'U').toUpperCase();
                            })()}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{sub.user_name || 'No name'}</span>
                            <span className="user-email">{sub.user_email}</span>
                            {sub.company && <span className="user-company">{sub.company}</span>}
                            {/* B2B Info Badges */}
                            {sub.discount_percent > 0 && (
                              <span className="admin-badge admin-badge--info admin-badge--sm" title="Discount Applied">
                                -{sub.discount_percent}%
                              </span>
                            )}
                            {sub.stripe_invoice_id && (
                              <span 
                                className={`admin-badge admin-badge--sm ${sub.invoice_paid_at ? 'admin-badge--success' : 'admin-badge--warning'}`}
                                title={sub.invoice_paid_at ? `Paid: ${formatDateTime(sub.invoice_paid_at)}` : `Due: ${formatDate(sub.invoice_due_date)}`}
                              >
                                {sub.invoice_paid_at ? 'Paid' : 'Awaiting Payment'}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`admin-badge ${getTierBadgeClass(sub.tier)}`}>
                          {sub.tier}
                        </span>
                      </td>
                      <td>
                        <span className={`admin-badge ${getStatusBadgeClass(sub.status)}`}>
                          {sub.status}
                        </span>
                      </td>
                      <td>
                        <span className={`admin-badge ${getApprovalBadgeClass(sub.approval_status)}`}>
                          {getApprovalBadgeLabel(sub.approval_status)}
                        </span>
                      </td>
                      <td>{formatAmount(sub.amount)}</td>
                      <td>
                        <span title={formatDateTime(sub.current_period_end)}>
                          {formatDate(sub.current_period_end)}
                        </span>
                      </td>
                      <td>{sub.last_login ? formatDateTime(sub.last_login, 'de-DE') : 'Never'}</td>
                      <td>
                        <span title={formatDateTime(sub.created_at)}>
                          {getTimeSince(sub.created_at)}
                        </span>
                      </td>
                      <td>
                        <div className="admin-table-actions">
                          {/* PI expiration warning for pending card payments */}
                          {isPending && sub.payment_intent_created_at && (() => {
                            const piAge = (Date.now() - new Date(sub.payment_intent_created_at).getTime()) / (1000 * 60 * 60 * 24);
                            if (piAge > 7) return (
                              <span className="admin-badge admin-badge--danger admin-badge--sm" title="Payment authorization expired -- will be auto-declined">
                                PI Expired
                              </span>
                            );
                            if (piAge > 5) return (
                              <span className="admin-badge admin-badge--warning admin-badge--sm" title={`Payment authorization expires in ${Math.ceil(7 - piAge)} day(s)`}>
                                PI Expiring
                              </span>
                            );
                            return null;
                          })()}

                          {/* Quick Actions button */}
                          {(canApproveDecline || canManageFinancial) && (
                            <button
                              className="admin-icon-btn admin-icon-btn--primary"
                              title="Quick Actions"
                              onClick={() => openQuickActionsModal(sub)}
                              disabled={processingId === sub.user_id}
                            >
                              {processingId === sub.user_id ? (
                                <div className="admin-btn-spinner"></div>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                </svg>
                              )}
                            </button>
                          )}

                          {/* Edit button (admin and finance only) */}
                          {canManageFinancial && (
                            <button
                              className="admin-icon-btn"
                              title="Edit Subscription"
                              onClick={() => openModifyModal(sub)}
                              disabled={processingId === sub.user_id}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > limit && (
            <div className="admin-pagination">
              <button
                className="admin-btn admin-btn--secondary admin-btn--sm"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
              >
                ← Previous
              </button>
              <span className="admin-pagination-info">
                Showing {offset + 1} - {Math.min(offset + limit, totalCount)} of {totalCount}
              </span>
              <button
                className="admin-btn admin-btn--secondary admin-btn--sm"
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= totalCount}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Quick Actions Modal */}
      {quickActionsModal.open && quickActionsModal.subscription && (() => {
        const sub = quickActionsModal.subscription;
        const qaPending = sub.approval_status === 'PENDING';
        const qaQuoteSent = sub.approval_status === 'QUOTE_SENT';
        const qaQuoteAccepted = sub.approval_status === 'QUOTE_ACCEPTED';
        const qaDeclined = sub.approval_status === 'DECLINED';
        const qaActive = sub.status === 'ACTIVE';
        const hasApprovalRequest = qaPending || qaQuoteSent || qaQuoteAccepted;
        const isCardPayment = sub.payment_method === 'CARD' || sub.payment_method === 'card';
        const isInvoice = sub.payment_method === 'INVOICE' || sub.payment_method === 'invoice';

        return (
          <div className="admin-modal-overlay" onClick={closeQuickActionsModal}>
            <div className="admin-modal admin-modal--glass" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
              <div className="admin-modal-header">
                <h3>Quick Actions — {sub.user_email}</h3>
                <button className="admin-modal-close" onClick={closeQuickActionsModal}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div className="admin-modal-body">

                {/* AREA: Request Approval */}
                {hasApprovalRequest && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #aaa)', marginBottom: '0.75rem', borderBottom: '1px solid var(--border-color, #333)', paddingBottom: '0.5rem' }}>
                      Request Approval
                    </h4>

                    {/* User info collapsed */}
                    <div style={{
                      background: 'var(--bg-tertiary, #1a1a2e)',
                      border: '1px solid var(--border-color, #333)',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      marginBottom: '12px',
                      fontSize: '0.82rem',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      lineHeight: '1.6'
                    }}>
                      <div><strong>Name:</strong> {sub.user_name || 'N/A'}</div>
                      <div><strong>Email:</strong> {sub.user_email}</div>
                      {sub.company && <div><strong>Company:</strong> {sub.company}</div>}
                      {sub.quote_company_country && <div><strong>Country:</strong> {sub.quote_company_country}</div>}
                      {sub.quote_tax_id && <div><strong>Tax ID:</strong> {sub.quote_tax_id}</div>}
                      <div><strong>Tier:</strong> {sub.tier} | <strong>Method:</strong> {sub.payment_method || 'N/A'}</div>
                      <div><strong>Registered:</strong> {formatDateTime(sub.created_at)}</div>
                      {sub.email_verified !== undefined && <div><strong>Email Verified:</strong> {sub.email_verified ? 'Yes' : 'No'}</div>}
                    </div>

                    {/* PENDING + Card Payment: Approve / Decline */}
                    {qaPending && isCardPayment && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="admin-btn admin-btn--success"
                          style={{ flex: 1 }}
                          onClick={() => { closeQuickActionsModal(); handleApprove(sub.user_id, sub.user_email); }}
                          disabled={processingId === sub.user_id}
                        >
                          Approve
                        </button>
                        <button
                          className="admin-btn admin-btn--danger"
                          style={{ flex: 1 }}
                          onClick={() => { closeQuickActionsModal(); openDeclineModal(sub.user_id, sub.user_email); }}
                          disabled={processingId === sub.user_id}
                        >
                          Decline
                        </button>
                      </div>
                    )}

                    {/* PENDING + Invoice (Quote Request): Review Quote */}
                    {qaPending && isInvoice && (
                      <button
                        className="admin-btn admin-btn--primary"
                        style={{ width: '100%' }}
                        onClick={() => { closeQuickActionsModal(); openQuoteReviewModal(sub); }}
                        disabled={processingId === sub.user_id}
                      >
                        Review Quote Request
                      </button>
                    )}

                    {/* PENDING + has quote request data: treat as B2B quote flow */}
                    {qaPending && !isCardPayment && !isInvoice && sub.quote_request_data && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="admin-btn admin-btn--primary"
                          style={{ flex: 1 }}
                          onClick={() => { closeQuickActionsModal(); openQuoteReviewModal(sub); }}
                        >
                          Review Quote Request
                        </button>
                        <button
                          className="admin-btn admin-btn--danger"
                          style={{ flex: 1 }}
                          onClick={() => { closeQuickActionsModal(); openDeclineModal(sub.user_id, sub.user_email); }}
                        >
                          Decline
                        </button>
                      </div>
                    )}

                    {/* PENDING + no payment method + no quote data: show approve/decline */}
                    {qaPending && !isCardPayment && !isInvoice && !sub.quote_request_data && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="admin-btn admin-btn--success"
                          style={{ flex: 1 }}
                          onClick={() => { closeQuickActionsModal(); handleApprove(sub.user_id, sub.user_email); }}
                        >
                          Approve
                        </button>
                        <button
                          className="admin-btn admin-btn--danger"
                          style={{ flex: 1 }}
                          onClick={() => { closeQuickActionsModal(); openDeclineModal(sub.user_id, sub.user_email); }}
                        >
                          Decline
                        </button>
                      </div>
                    )}

                    {/* QUOTE_SENT: Status + Resend + Decline */}
                    {qaQuoteSent && (
                      <>
                        {sub.quote_expires_at && (
                          <div style={{ marginBottom: '8px', fontSize: '0.82rem', color: new Date(sub.quote_expires_at) < new Date() ? '#ef4444' : 'var(--text-secondary, #aaa)' }}>
                            Quote {new Date(sub.quote_expires_at) < new Date() ? 'expired' : 'expires'}: {formatDate(sub.quote_expires_at)}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="admin-btn admin-btn--primary"
                            style={{ flex: 1 }}
                            onClick={() => {
                              closeQuickActionsModal();
                              if (confirm(`Resend quote email to ${sub.user_email}?`)) {
                                setProcessingId(sub.user_id);
                                reviewQuote(sub.user_id, { action: 'resend' })
                                  .then(() => { setSuccess(`Quote resent to ${sub.user_email}`); setTimeout(() => setSuccess(''), 5000); loadSubscriptions(); })
                                  .catch(err => setError(err.message))
                                  .finally(() => setProcessingId(null));
                              }
                            }}
                          >
                            Resend Quote
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            style={{ flex: 1 }}
                            onClick={() => { closeQuickActionsModal(); openDeclineModal(sub.user_id, sub.user_email); }}
                          >
                            Decline
                          </button>
                        </div>
                      </>
                    )}

                    {/* QUOTE_ACCEPTED: Confirm Payment + Send Reminder */}
                    {qaQuoteAccepted && (
                      <>
                        {sub.quote_accepted_at && (
                          <div style={{ marginBottom: '8px', fontSize: '0.82rem', color: 'var(--text-secondary, #aaa)' }}>
                            Accepted {(() => { const d = Math.floor((Date.now() - new Date(sub.quote_accepted_at).getTime()) / 86400000); return `${d} day${d !== 1 ? 's' : ''} ago`; })()}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="admin-btn admin-btn--success"
                            style={{ flex: 1 }}
                            onClick={() => { closeQuickActionsModal(); handleConfirmPayment(sub.user_id, sub.user_email); }}
                          >
                            Confirm Payment
                          </button>
                          <button
                            className="admin-btn admin-btn--warning"
                            style={{ flex: 1 }}
                            onClick={() => { closeQuickActionsModal(); handleSendReminder(sub.user_id, sub.user_email, sub.company); }}
                          >
                            Send Reminder
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* AREA: Declined - Re-open */}
                {qaDeclined && canApproveDecline && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #aaa)', marginBottom: '0.75rem', borderBottom: '1px solid var(--border-color, #333)', paddingBottom: '0.5rem' }}>
                      Declined Request
                    </h4>
                    {sub.decline_reason && (
                      <div style={{ marginBottom: '8px', fontSize: '0.82rem', color: '#ef4444' }}>
                        Reason: {sub.decline_reason}
                      </div>
                    )}
                    <button
                      className="admin-btn admin-btn--primary"
                      style={{ width: '100%' }}
                      onClick={() => {
                        closeQuickActionsModal();
                        if (confirm(`Re-open subscription request for ${sub.user_email}? This will set status back to Pending for review.`)) {
                          setProcessingId(sub.user_id);
                          reviewQuote(sub.user_id, { action: 'reopen' })
                            .then(() => { setSuccess(`Request re-opened for ${sub.user_email}`); setTimeout(() => setSuccess(''), 5000); loadSubscriptions(); })
                            .catch(err => setError(err.message))
                            .finally(() => setProcessingId(null));
                        }
                      }}
                    >
                      Re-open as Pending
                    </button>
                  </div>
                )}

                {/* AREA: Quick Actions (always shown) */}
                <div>
                  <h4 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #aaa)', marginBottom: '0.75rem', borderBottom: '1px solid var(--border-color, #333)', paddingBottom: '0.5rem' }}>
                    Quick Actions
                  </h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {canManageFinancial && (
                      <button
                        className="admin-btn admin-btn--danger"
                        style={{ flex: 1, minWidth: '140px' }}
                        onClick={() => { closeQuickActionsModal(); openRevokeModal(sub); }}
                      >
                        Cancel Subscription
                      </button>
                    )}
                    {canManageFinancial && (
                      <button
                        className="admin-btn admin-btn--primary"
                        style={{ flex: 1, minWidth: '100px' }}
                        onClick={() => openGiveAccessModal(sub, 'PRO')}
                      >
                        Give PRO
                      </button>
                    )}
                    {canManageFinancial && (
                      <button
                        className="admin-btn admin-btn--secondary"
                        style={{ flex: 1, minWidth: '100px', borderColor: '#0d7c99', color: '#0d7c99' }}
                        onClick={() => openGiveAccessModal(sub, 'DEMO')}
                      >
                        Give Demo
                      </button>
                    )}
                    {qaActive && sub.cancel_at_period_end && canManageFinancial && (
                      <button
                        className="admin-btn admin-btn--success"
                        style={{ flex: 1, minWidth: '140px' }}
                        onClick={() => {
                          closeQuickActionsModal();
                          if (confirm(`Reactivate subscription for ${sub.user_email}? This will resume auto-renewal.`)) {
                            setProcessingId(sub.user_id);
                            adminReactivateSubscription(sub.user_id).then(() => {
                              setSuccess(`Subscription reactivated for ${sub.user_email}`);
                              setTimeout(() => setSuccess(''), 5000);
                              loadSubscriptions();
                            }).catch(err => {
                              setError(err.message);
                            }).finally(() => setProcessingId(null));
                          }
                        }}
                      >
                        Reactivate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Give Access Modal */}
      {giveAccessModal.open && giveAccessModal.subscription && (
        <div className="admin-modal-overlay" onClick={closeGiveAccessModal}>
          <div className="admin-modal admin-modal--glass" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="admin-modal-header">
              <h3>Give {giveAccessModal.tier} Access</h3>
              <button className="admin-modal-close" onClick={closeGiveAccessModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-text">
                Grant <strong>{giveAccessModal.tier}</strong> access to <strong>{giveAccessModal.subscription.user_email}</strong>
              </p>
              <div className="admin-form-group">
                <label className="admin-form-label">Access Until</label>
                <input
                  type="date"
                  className="admin-form-input"
                  value={giveAccessForm.until}
                  onChange={(e) => setGiveAccessForm(prev => ({ ...prev, until: e.target.value }))}
                />
                <span className="admin-form-hint">
                  {giveAccessModal.tier === 'PRO'
                    ? 'Default: 1 year from today'
                    : `Default: ${demoDurationDays} days from today (configured demo length)`}
                </span>
              </div>
              <div className="admin-form-group">
                {giveAccessModal.subscription.subscription_id == null ? (
                  <span className="admin-form-hint" title="This user has no subscription row yet, so this grant goes through the /grant endpoint, which always sends the activation email.">
                    This user has no existing subscription — an activation email will always be sent.
                  </span>
                ) : (
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={giveAccessForm.notify_user}
                      onChange={(e) => setGiveAccessForm(prev => ({ ...prev, notify_user: e.target.checked }))}
                    />
                    <span>Send activation email to user</span>
                  </label>
                )}
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--secondary" onClick={closeGiveAccessModal}>Cancel</button>
              <button
                className="admin-btn admin-btn--primary"
                onClick={handleGiveAccess}
                disabled={!giveAccessForm.until}
              >
                Grant {giveAccessModal.tier} Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Access to User Modal — search by name/email to reach a
          subscription-less user, then grant PRO/DEMO via the Give Access
          modal (routed through /grant instead of /modify). */}
      {userLookupModal.open && (
        <div className="admin-modal-overlay" onClick={closeUserLookupModal}>
          <div className="admin-modal admin-modal--glass" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="admin-modal-header">
              <h3>Grant Access to User</h3>
              <button className="admin-modal-close" onClick={closeUserLookupModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-text">
                Search for a user by name or email. Use this to grant PRO/DEMO access to
                accounts that don't have a subscription yet (they won't appear in the main list above).
              </p>
              <div className="admin-search-wrapper" style={{ marginBottom: '12px' }}>
                <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="Search by email or name…"
                  value={userLookupQuery}
                  onInput={(e) => handleUserLookupInput(e.target.value)}
                  onKeyDown={handleUserLookupKeyDown}
                  autoFocus
                />
              </div>

              {userLookupLoading && (
                <div className="admin-loading" style={{ padding: '12px 0' }}>
                  <div className="admin-spinner"></div>
                  <p>Searching…</p>
                </div>
              )}

              {!userLookupLoading && userLookupError && (
                <p className="admin-modal-text admin-modal-text--error">{userLookupError}</p>
              )}

              {!userLookupLoading && !userLookupError && userLookupQuery.trim() && userLookupResults.length === 0 && (
                <p className="admin-modal-text">No users found matching "{userLookupQuery}".</p>
              )}

              {!userLookupLoading && userLookupResults.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
                  {userLookupResults.map(user => (
                    <div
                      key={user.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'var(--bg-tertiary, #1a1a2e)',
                        border: '1px solid var(--border-color, #333)',
                        borderRadius: '6px'
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div className="user-name" style={{ fontSize: '0.9rem' }}>{user.full_name || 'No name'}</div>
                        <div className="user-email" style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #aaa)' }}>{user.email}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          className="admin-btn admin-btn--primary admin-btn--sm"
                          onClick={() => handlePickUserForGrant(user, 'PRO')}
                        >
                          Grant PRO
                        </button>
                        <button
                          className="admin-btn admin-btn--secondary admin-btn--sm"
                          style={{ borderColor: '#0d7c99', color: '#0d7c99' }}
                          onClick={() => handlePickUserForGrant(user, 'DEMO')}
                        >
                          Grant DEMO
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {declineModal.open && (
        <div className="admin-modal-overlay" onClick={closeDeclineModal}>
          <div className="admin-modal admin-modal--glass" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Decline Subscription Request</h3>
              <button className="admin-modal-close" onClick={closeDeclineModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-text">
                You are about to decline the subscription request for <strong>{declineModal.email}</strong>.
              </p>
              <div className="admin-form-group">
                <label className="admin-form-label">Reason for declining *</label>
                <textarea 
                  className="admin-form-textarea"
                  placeholder="Enter reason for declining this request..."
                  value={declineReason}
                  onInput={(e) => setDeclineReason(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--secondary" onClick={closeDeclineModal}>
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn--danger" 
                onClick={handleDecline}
                disabled={!declineReason.trim()}
              >
                Decline Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {revokeModal.open && (
        <div className="admin-modal-overlay" onClick={closeRevokeModal}>
          <div className="admin-modal admin-modal--glass admin-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header admin-modal-header--danger">
              <h3>Revoke Subscription</h3>
              <button className="admin-modal-close" onClick={closeRevokeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-warning">
                <p>You are about to revoke the <strong>{revokeModal.tier}</strong> subscription for:</p>
                <p className="admin-modal-email">{revokeModal.email}</p>
                {revokeModal.amount > 0 && (
                  <p className="admin-modal-amount">Subscription Amount: <strong>{formatAmount(revokeModal.amount)}</strong></p>
                )}
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label">Reason (optional)</label>
                <textarea 
                  className="admin-form-textarea"
                  placeholder="Enter reason for revoking this subscription..."
                  value={revokeForm.reason}
                  onInput={(e) => setRevokeForm(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label">Refund Type</label>
                <div className="admin-refund-options">
                  {REFUND_OPTIONS.map(opt => (
                    <label 
                      key={opt.value}
                      className={`admin-refund-option ${revokeForm.refund_type === opt.value ? 'admin-refund-option--selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="refund_type"
                        value={opt.value}
                        checked={revokeForm.refund_type === opt.value}
                        onChange={(e) => setRevokeForm(prev => ({ ...prev, refund_type: e.target.value }))}
                      />
                      <div className="admin-refund-option-content">
                        <span className="admin-refund-option-label">{opt.label}</span>
                        <span className="admin-refund-option-desc">{opt.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="admin-form-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={revokeForm.notify_user}
                    onChange={(e) => setRevokeForm(prev => ({ ...prev, notify_user: e.target.checked }))}
                  />
                  <span>Send email notification to user</span>
                </label>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--secondary" onClick={closeRevokeModal}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn--danger"
                onClick={handleRevoke}
              >
                Revoke Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Modal */}
      {modifyModal.open && modifyModal.subscription && (
        <div className="admin-modal-overlay" onClick={closeModifyModal}>
          <div className="admin-modal admin-modal--glass admin-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Subscription</h3>
              <button className="admin-modal-close" onClick={closeModifyModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-text">
                Editing subscription for <strong>{modifyModal.subscription.user_email}</strong>
              </p>
              
              {/* B2B Info Section - only show if subscription has B2B data */}
              {(modifyModal.subscription.stripe_quote_id || modifyModal.subscription.stripe_invoice_id || modifyModal.subscription.discount_percent > 0) && (
                <div className="admin-quote-info" style={{ marginBottom: '1.5rem' }}>
                  <h4>🏢 B2B Payment Info</h4>
                  <div className="admin-quote-info-grid">
                    {modifyModal.subscription.discount_percent > 0 && (
                      <div className="admin-quote-info-item">
                        <span className="admin-quote-info-label">Discount</span>
                        <span className="admin-quote-info-value">-{modifyModal.subscription.discount_percent}%</span>
                      </div>
                    )}
                    {modifyModal.subscription.quote_accepted_at && (
                      <div className="admin-quote-info-item">
                        <span className="admin-quote-info-label">Quote Accepted</span>
                        <span className="admin-quote-info-value">{formatDateTime(modifyModal.subscription.quote_accepted_at)}</span>
                      </div>
                    )}
                    {modifyModal.subscription.invoice_due_date && (
                      <div className="admin-quote-info-item">
                        <span className="admin-quote-info-label">Payment Due</span>
                        <span className="admin-quote-info-value">{formatDate(modifyModal.subscription.invoice_due_date)}</span>
                      </div>
                    )}
                    {modifyModal.subscription.invoice_paid_at && (
                      <div className="admin-quote-info-item">
                        <span className="admin-quote-info-label">Paid On</span>
                        <span className="admin-quote-info-value admin-text--success">{formatDateTime(modifyModal.subscription.invoice_paid_at)}</span>
                      </div>
                    )}
                    {modifyModal.subscription.stripe_invoice_id && !modifyModal.subscription.invoice_paid_at && (
                      <div className="admin-quote-info-item admin-quote-info-item--full">
                        <span className="admin-badge admin-badge--warning">Awaiting Bank Transfer Payment</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Tier</label>
                  <select 
                    className="admin-form-select"
                    value={modifyForm.tier}
                    onChange={(e) => setModifyForm(prev => ({ ...prev, tier: e.target.value }))}
                  >
                    <option value="FREE">Free</option>
                    <option value="PRO">Pro</option>
                    <option value="DEMO">Demo</option>
                  </select>
                </div>
                
                <div className="admin-form-group">
                  <label className="admin-form-label">Status</label>
                  <select
                    className="admin-form-select"
                    value={modifyForm.status}
                    onChange={(e) => setModifyForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="CANCELLED">Cancelled</option>
                    <option value="TRIALING">Trialing</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Approval Status</label>
                  <select
                    className="admin-form-select"
                    value={modifyForm.approval_status}
                    onChange={(e) => setModifyForm(prev => ({ ...prev, approval_status: e.target.value }))}
                  >
                    <option value="NONE">None</option>
                    <option value="PENDING">Pending</option>
                    <option value="QUOTE_SENT">Quote Sent</option>
                    <option value="QUOTE_ACCEPTED">Quote Accepted</option>
                    <option value="APPROVED">Approved</option>
                    <option value="DECLINED">Declined</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Subscription End Date</label>
                  <input
                    type="date"
                    className="admin-form-input"
                    value={modifyForm.current_period_end}
                    onChange={(e) => setModifyForm(prev => ({ ...prev, current_period_end: e.target.value }))}
                  />
                  <span className="admin-form-hint">Leave empty to keep current value</span>
                </div>
              </div>
              
              <div className="admin-form-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={modifyForm.notify_user}
                    onChange={(e) => setModifyForm(prev => ({ ...prev, notify_user: e.target.checked }))}
                  />
                  <span>Send email notification about changes</span>
                </label>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--secondary" onClick={closeModifyModal}>
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn--primary" 
                onClick={handleModify}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Review Modal (B2B workflow) */}
      {quoteReviewModal.open && quoteReviewModal.subscription && (
        <div className="admin-modal-overlay" onClick={closeQuoteReviewModal}>
          <div className="admin-modal admin-modal--glass admin-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Review B2B Quote Request</h3>
              <button className="admin-modal-close" onClick={closeQuoteReviewModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="admin-modal-body">
              {/* Company & User Info */}
              <div className="admin-quote-info">
                <h4>Company Details</h4>
                <div className="admin-quote-info-grid">
                  <div className="admin-quote-info-item">
                    <span className="admin-quote-info-label">User</span>
                    <span className="admin-quote-info-value">{quoteReviewModal.subscription.user_email}</span>
                  </div>
                  {quoteReviewModal.subscription.quote_company_name && (
                    <div className="admin-quote-info-item">
                      <span className="admin-quote-info-label">Company</span>
                      <span className="admin-quote-info-value">{quoteReviewModal.subscription.quote_company_name}</span>
                    </div>
                  )}
                  {quoteReviewModal.subscription.quote_company_country && (
                    <div className="admin-quote-info-item">
                      <span className="admin-quote-info-label">Country</span>
                      <span className="admin-quote-info-value">{quoteReviewModal.subscription.quote_company_country}</span>
                    </div>
                  )}
                  {quoteReviewModal.subscription.quote_tax_id && (
                    <div className="admin-quote-info-item">
                      <span className="admin-quote-info-label">Tax ID</span>
                      <span className="admin-quote-info-value">
                        {quoteReviewModal.subscription.quote_tax_id}
                        {quoteReviewModal.subscription.vat_validation_status && (
                          <span className={`admin-vat-status admin-vat-status--${quoteReviewModal.subscription.vat_validation_status}`}>
                            {quoteReviewModal.subscription.vat_validation_status === 'valid' ? '✓ Verified' : '⚠ Unverified'}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {quoteReviewModal.subscription.quote_contact_email && (
                    <div className="admin-quote-info-item">
                      <span className="admin-quote-info-label">Contact Email</span>
                      <span className="admin-quote-info-value">{quoteReviewModal.subscription.quote_contact_email}</span>
                    </div>
                  )}
                  {quoteReviewModal.subscription.quote_notes && (
                    <div className="admin-quote-info-item admin-quote-info-item--full">
                      <span className="admin-quote-info-label">Notes</span>
                      <span className="admin-quote-info-value">{quoteReviewModal.subscription.quote_notes}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Selection */}
              <div className="admin-form-group">
                <label className="admin-form-label">Action</label>
                <div className="admin-action-toggle">
                  <label className={`admin-action-option ${quoteReviewForm.action === 'approve' ? 'admin-action-option--active' : ''}`}>
                    <input 
                      type="radio" 
                      value="approve" 
                      checked={quoteReviewForm.action === 'approve'}
                      onChange={() => setQuoteReviewForm(prev => ({ ...prev, action: 'approve' }))}
                    />
                    <span>✓ Approve & Send Quote</span>
                  </label>
                  <label className={`admin-action-option admin-action-option--danger ${quoteReviewForm.action === 'decline' ? 'admin-action-option--active' : ''}`}>
                    <input 
                      type="radio" 
                      value="decline" 
                      checked={quoteReviewForm.action === 'decline'}
                      onChange={() => setQuoteReviewForm(prev => ({ ...prev, action: 'decline' }))}
                    />
                    <span>✗ Decline Request</span>
                  </label>
                </div>
              </div>
              
              {/* Approve Options */}
              {quoteReviewForm.action === 'approve' && (() => {
                const sub = quoteReviewModal.subscription;
                const promoPercent = sub.promo_discount_percent || 0;
                const adminPercent = quoteReviewForm.discount_percent || 0;
                const totalDiscount = Math.min(promoPercent + adminPercent, 100);
                const hasCustomAmount = !!quoteReviewForm.custom_amount;
                const finalPrice = hasCustomAmount
                  ? parseFloat(quoteReviewForm.custom_amount) || 0
                  : proBasePrice * (1 - totalDiscount / 100);

                return (
                <div className="admin-approve-options">
                  <h4>Pricing Options</h4>

                  {/* Promo code info */}
                  {promoPercent > 0 && (
                    <div style={{
                      background: 'rgba(13,124,153,0.1)',
                      border: '1px solid rgba(13,124,153,0.3)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span className="admin-badge admin-badge--info admin-badge--sm">Promo</span>
                      <span style={{ fontSize: '0.9rem', color: 'var(--text-primary, #e0e0e0)' }}>
                        {sub.promo_code_name ? <strong>{sub.promo_code_name}</strong> : 'Applied'}{' '}
                        — {promoPercent}% discount
                      </span>
                    </div>
                  )}

                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label className="admin-form-label">
                        Admin Additional Discount (%)
                        {promoPercent > 0 && (
                          <span style={{ fontWeight: 'normal', fontSize: '0.8rem', color: 'var(--text-secondary, #aaa)', marginLeft: '6px' }}>
                            on top of {promoPercent}% promo
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        className="admin-form-input"
                        min="0"
                        max={100 - promoPercent}
                        value={quoteReviewForm.discount_percent}
                        onChange={(e) => {
                          setQuoteReviewForm(prev => ({
                            ...prev,
                            discount_percent: Number(e.target.value),
                            custom_amount: '' // Clear custom amount when discount changes
                          }));
                        }}
                        disabled={hasCustomAmount}
                      />
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Or Custom Amount (€)</label>
                      <input
                        type="number"
                        className="admin-form-input"
                        min="0"
                        step="0.01"
                        value={quoteReviewForm.custom_amount}
                        onChange={(e) => {
                          setQuoteReviewForm(prev => ({
                            ...prev,
                            custom_amount: e.target.value,
                            discount_percent: 0 // Clear discount when custom amount changes
                          }));
                        }}
                        placeholder="Leave empty for standard/discounted price"
                      />
                    </div>
                  </div>

                  {/* Combined discount preview */}
                  <div className="admin-price-preview" style={{
                    background: 'rgba(234,91,12,0.08)',
                    border: '1px solid rgba(234,91,12,0.2)',
                    borderRadius: '8px',
                    padding: '12px 16px'
                  }}>
                    {!hasCustomAmount ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-secondary, #aaa)' }}>
                          <span>Base price:</span>
                          <span>{'\u20AC'}{proBasePrice.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {promoPercent > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#0d7c99' }}>
                            <span>Promo discount:</span>
                            <span>-{promoPercent}%</span>
                          </div>
                        )}
                        {adminPercent > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#ea5b0c' }}>
                            <span>Admin discount:</span>
                            <span>-{adminPercent}%</span>
                          </div>
                        )}
                        {(promoPercent > 0 || adminPercent > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-secondary, #aaa)', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '4px' }}>
                            <span>Total discount:</span>
                            <span>-{totalDiscount}%</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary, #fff)', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '6px', marginTop: '2px' }}>
                          <span>Final price:</span>
                          <span>{'\u20AC'}{finalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>Custom amount:</span>
                        <span className="admin-price-preview-amount">{'\u20AC'}{finalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}
              
              {/* Decline Options */}
              {quoteReviewForm.action === 'decline' && (
                <div className="admin-decline-options">
                  <div className="admin-form-group">
                    <label className="admin-form-label">Reason (shown to customer) *</label>
                    <textarea
                      className="admin-form-textarea"
                      value={quoteReviewForm.decline_reason}
                      onChange={(e) => setQuoteReviewForm(prev => ({ ...prev, decline_reason: e.target.value }))}
                      placeholder="Please provide a reason for declining..."
                      rows={3}
                    />
                  </div>
                </div>
              )}
              
              {/* Admin Notes */}
              <div className="admin-form-group">
                <label className="admin-form-label">Internal Notes (not shown to customer)</label>
                <textarea
                  className="admin-form-textarea"
                  value={quoteReviewForm.admin_notes}
                  onChange={(e) => setQuoteReviewForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                  placeholder="Internal notes for record keeping..."
                  rows={2}
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={quoteReviewForm.notify_user}
                    onChange={(e) => setQuoteReviewForm(prev => ({ ...prev, notify_user: e.target.checked }))}
                  />
                  <span>Send email notification to customer</span>
                </label>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--secondary" onClick={closeQuoteReviewModal}>
                Cancel
              </button>
              <button 
                className={`admin-btn ${quoteReviewForm.action === 'approve' ? 'admin-btn--primary' : 'admin-btn--danger'}`}
                onClick={handleQuoteReview}
                disabled={quoteReviewForm.action === 'decline' && !quoteReviewForm.decline_reason.trim()}
              >
                {quoteReviewForm.action === 'approve' ? 'Approve & Send Quote' : 'Decline Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
